// Lib
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import { defer } from 'lodash';

// Actions
import { deselectAllElements } from '../../selection/selectionActions';

// Components
import { Sheet } from '../../../mobile/structural/sheet/Sheet';
import DocumentModalTiptapEditor from './DocumentModalTiptapEditor';
import AttachedCommentsContainer from '../../attachedComment/AttachedCommentsContainer';

// Hooks
import useSheet from '../../../mobile/structural/sheet/hooks/useSheet';
import useTipTapDocumentEditingProps from './useTipTapDocumentEditingProps';
import { useElementTiptapContent } from '../../../../common/tiptap/conversion/elementConversion/useElementTiptapContent';

// Utils
import { getTextContent } from '../../../../common/elements/utils/elementPropertyUtils';
import { getCurrentBoardId } from '../../selectors/currentBoardSelector';
import { getEditorDomElementId } from '../../utils/elementUtil';
import doesEditorJsonHaveText from '../../../../common/tiptap/utils/jsonContentUtils/doesEditorJsonHaveText';
import { isLocationAttachedInline } from '../../../../common/elements/utils/elementLocationUtils';

// Types
import { ImMNElement } from '../../../../common/elements/elementModelTypes';
import { TiptapContent } from '../../../../common/tiptap/tiptapTypes';
import { SheetId } from '../../../mobile/structural/sheet/sheetTypes';
import { DocumentType } from '../../../../common/tiptap/conversion/elementConversion/elementConversion';
import { GRID } from '../../../utils/grid/gridConstants';

// Constants
import { DOCUMENT_MODAL_TITLE_EDITOR_KEY } from './documentModalConstants';
import EditableTitle from '../../../components/editableTitle/EditableTitle';

// Styles
import './MobileDocumentSheet.scss';
import { useTranslator } from "../../../components/i18n/useTranslator";

interface MobileDocumentModalProps {
    close: () => void;
    elementId: string;
    element: ImMNElement;
    textContent: TiptapContent | null;
    isEditable: boolean;
    isPreview: boolean;
    permissions: number;
}

const MobileDocumentSheet = (props: MobileDocumentModalProps) => {
    const { close, element, elementId, isEditable, isPreview, permissions } = props;

    const currentBoardId = useSelector(getCurrentBoardId);

    const sheetProps = useSheet(SheetId.Document, undefined, { openByDefault: true, onCloseTransitionEnd: close });
    const tipTapDocumentEditingProps = useTipTapDocumentEditingProps(elementId, element);
    const textContent = useElementTiptapContent(getTextContent(element), elementId, DocumentType.textContent);

    const translate = useTranslator('elements', 'elementTypes.document');

    const { isEditing, dispatchStartEditingDocument } = tipTapDocumentEditingProps;

    // Delay the start editing action - to allow the title editing to end before switching to
    // the main editor, otherwise the finish editing action would apply to the main content too
    const delayedStartEditingMainContent = () => {
        defer(dispatchStartEditingDocument);
    };

    const dispatch = useDispatch();
    const dispatchDeselectElements = () => {
        dispatch(deselectAllElements());
    };

    const classes = classNames('MobileDocumentSheet', {
        preview: isPreview,
        'read-only': !isEditable,
    });

    const startEditIfEmpty = () => {
        if (!!textContent && doesEditorJsonHaveText(textContent)) return;

        delayedStartEditingMainContent();
    };

    return (
        <Sheet
            {...sheetProps}
            defaultSnapPoint={1}
            onCloseTransitionStart={dispatchDeselectElements}
            onOpenTransitionEnd={startEditIfEmpty}
            showToolbar={isEditing && !isPreview}
            className={classes}
        >
            <Sheet.Overlay />
            <Sheet.Panel>
                <Sheet.Header>
                    <Sheet.Heading>
                        <EditableTitle
                            {...props}
                            isEditable={isEditable}
                            editorKey={DOCUMENT_MODAL_TITLE_EDITOR_KEY}
                            initialValue={translate('title.placeholder')}
                            onStopEditing={delayedStartEditingMainContent}
                        />
                    </Sheet.Heading>
                    <Sheet.CloseButton>{translate('sheet.close')}</Sheet.CloseButton>
                </Sheet.Header>
                <Sheet.Content>
                    <div id="document-modal-content-container">
                        <div id="document-modal-content-scrollable-container" data-scrollable={true}>
                            <div id="document-modal-content" className="content">
                                <div id={getEditorDomElementId(elementId)}>
                                    <DocumentModalTiptapEditor
                                        {...props}
                                        {...tipTapDocumentEditingProps}
                                        textContent={textContent}
                                        isMobile
                                        isEditable={isEditable}
                                    />
                                </div>
                            </div>
                        </div>
                        <AttachedCommentsContainer
                            gridSize={GRID.MOBILE.size}
                            elementId={elementId}
                            element={element}
                            isPresentational={false}
                            currentBoardId={currentBoardId}
                            permissions={permissions}
                            elementFilterFunction={isLocationAttachedInline}
                            popupCssSelectorPrefix="#document-modal-content "
                        />
                    </div>
                </Sheet.Content>
            </Sheet.Panel>
        </Sheet>
    );
};

export default MobileDocumentSheet;
